import {computed, inject, Injectable, signal} from '@angular/core';
import {Observable, catchError, throwError} from 'rxjs';
import {AuthService} from './auth.service';
import {UserProfileDto} from '../../../models/generated-models';
import {UserRole, normalizeUserRoles} from '../../app.constants';

/**
 * Single, app-wide source for the current user's personal profile and — most
 * importantly — their business role(s). This service consolidates the
 * previously scattered `getPersonalProfile()` calls + ad-hoc normalization so
 * every feature reads the same value the same way.
 *
 * The profile is fetched once and cached; call {@link refresh} after the user
 * edits their profile so dependent screens pick up the change within the SPA
 * session.
 */
@Injectable({providedIn: 'root'})
export class CurrentUserContextService {
  private readonly authService = inject(AuthService);
  private profile$: Observable<UserProfileDto> | null = null;

  private readonly _profile = signal<UserProfileDto | null>(null);
  readonly profile = this._profile.asReadonly();

  constructor() {
    // Mirror the shared profile (owned by AuthService) into the local signal so
    // every consumer keeps seeing the same value.
    this.authService.profile$.subscribe((profile) => {
      if (profile) {
        this._profile.set(profile);
      }
    });
  }

  /** Distinct, normalized business roles for the current user. */
  readonly userRoles = computed<UserRole[]>(() => normalizeUserRoles(this._profile()?.userRoles));

  /**
   * True only once the user has a saved (completed) personal profile. Stays false
   * for a freshly-provisioned user until they save their profile the first time —
   * used to hide profile sub-features (business/certificate tabs, delegation) until then.
   */
  readonly profileCompleted = computed<boolean>(() => this._profile()?.profileCompleted === true);

  /** Load (and cache) the current user's profile; subsequent calls reuse the cache. */
  load(): Observable<UserProfileDto> {
    if (!this.profile$) {
      this.profile$ = this.authService.loadUserProfile().pipe(
        catchError((error) => {
          this.profile$ = null; // allow a retry on the next load()
          return throwError(() => error);
        }),
      );
    }
    return this.profile$;
  }

  /** Drop the cache and re-fetch — call this after the profile is updated. */
  refresh(): Observable<UserProfileDto> {
    this.profile$ = this.authService.refreshUserProfile().pipe(
      catchError((error) => {
        this.profile$ = null;
        return throwError(() => error);
      }),
    );
    return this.profile$;
  }

  hasUserRole(role: UserRole): boolean {
    return this.userRoles().includes(role);
  }

  hasAnyAuthority(...roles: UserRole[]): boolean {
    return roles.some((role) => this.hasUserRole(role));
  }
}
